import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Toaster } from "sonner";
import { trackEvent } from "@aptabase/web";
import "@/index.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Curriculum from "@/pages/Curriculum";
import Topic from "@/pages/Topic";
import Interview from "@/pages/Interview";
import InterviewStart from "@/pages/InterviewStart";
import Familiar from "@/pages/Familiar";
import Gauntlet from "@/pages/Gauntlet";
import FlexCard from "@/pages/FlexCard";
import Dashboard from "@/pages/Dashboard";
import Pricing from "@/pages/Pricing";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import BillingSuccess from "@/pages/BillingSuccess";
import FreeLesson from "@/pages/FreeLesson";
import InterviewQueryAlternatives from "@/pages/InterviewQueryAlternatives";
import BestAiEngineerInterviewPrepPlatforms from "@/pages/BestAiEngineerInterviewPrepPlatforms";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-emerald-400 font-mono">
      <span className="cursor-blink">warming up</span>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } });
        localStorage.setItem("token", data.token);
        setUser(data.user);
        window.history.replaceState({}, "", "/");
        navigate(data.user.placement_completed ? "/curriculum" : "/onboarding", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <FullLoader />;
}

function Protected({ children, skipPlacement }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!skipPlacement && !user.placement_completed) return <Navigate to="/onboarding" replace />;
  return children;
}

function Shell() {
  const location = useLocation();
  useEffect(() => {
    trackEvent("page_view", { path: location.pathname });
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const hideNav = ["/login", "/register", "/onboarding"].includes(location.pathname);
  return (
    <div className="min-h-screen flex flex-col">
      {!hideNav && <Navbar />}
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/lesson" element={<FreeLesson />} />
          <Route path="/onboarding" element={<Protected skipPlacement><Onboarding /></Protected>} />
          <Route path="/curriculum" element={<Protected><Curriculum /></Protected>} />
          <Route path="/topic/:topicId" element={<Protected><Topic /></Protected>} />
          <Route path="/interview" element={<Protected><InterviewStart /></Protected>} />
          <Route path="/interview/:sessionId" element={<Protected><Interview /></Protected>} />
          <Route path="/familiar" element={<Protected><Familiar /></Protected>} />
          <Route path="/gauntlet" element={<Protected><Gauntlet /></Protected>} />
          <Route path="/card/:userId" element={<FlexCard />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/interview-query-alternatives" element={<InterviewQueryAlternatives />} />
          <Route path="/best-ai-engineer-interview-prep-platforms" element={<BestAiEngineerInterviewPrepPlatforms />} />
          <Route path="/account" element={<Protected skipPlacement><Account /></Protected>} />
          <Route path="/admin" element={<Protected skipPlacement><Admin /></Protected>} />
          <Route path="/billing/success" element={<Protected skipPlacement><BillingSuccess /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {!hideNav && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#0e1626", border: "1px solid #252f4c", color: "#ece6d8", borderRadius: 8, fontFamily: "Hanken Grotesk" } }} />
        <Shell />
      </BrowserRouter>
    </AuthProvider>
  );
}
